import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Spinner from '../../App/Components/Spinner/Spinner'
import DataStoreDataProvider from '../../App/Components/Table/DataProvider/DataStoreDataProvider'
import { default as TableComponent } from '../../App/Components/Table/Table'
import TableColumn from '../../App/Components/Table/TableColumn'
import { useAppDispatch, useAppSelector } from '../../App/Hooks/reduxHooks'
import { activeEstablishmentSelector } from '../../Establishment/Store/reducer'
import { getPreferences } from '../../Preference/Store/actions'
import ActivityDataStore from '../DataStore/ActivityDataStore'
import { getActivityCategories, getDepartments } from '../store/actions'
import { Link } from 'react-router-dom'

export const ActivityTable = () => {
  const [dataProvider, setDataProvider] = useState<any>()
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const { preferences, activityCategories, departments } = useAppSelector(
    state => ({
      preferences: state.preferences.list,
      activityCategories: state.activities.activityCategories.list,
      departments: state.activities.departments?.list,
    }),
  )

  const establishment = useAppSelector(activeEstablishmentSelector)

  useEffect(() => {
    if (!preferences) {
      dispatch(getPreferences(establishment.uuid))
    }
    if (!activityCategories) {
      dispatch(getActivityCategories())
    }
    if (!departments) {
      dispatch(getDepartments())
    }
    setDataProvider(
      new DataStoreDataProvider(
        ActivityDataStore,
        'getActivities',
        'updateActivity',
      ),
    )
  }, [])

  const categoryChoices = {}
  if (activityCategories) {
    for (let i = 0; i < activityCategories.length; i++) {
      categoryChoices[activityCategories[i].name] = activityCategories[i].id
    }
  }

  const departmentChoices = { 'No department': null }
  if (departments) {
    for (let i = 0; i < departments.length; i++) {
      departmentChoices[departments[i].name] = departments[i].id
    }
  }

  if (!preferences) {
    return <Spinner />
  }

  const calendarDivision = preferences.calendarDivision
    ? preferences.calendarDivision
    : 5

  const minuteChoices = {}
  for (let minutes = 0; minutes < 300; minutes += calendarDivision) {
    minuteChoices[t('{{amount}} minutes', { amount: minutes })] = minutes
  }

  const colors = [
    '#f44336',
    '#e91e63',
    '#9c27b0',
    '#673ab7',
    '#3f51b5',
    '#2196f3',
    '#03a9f4',
    '#00bcd4',
    '#009688',
    '#4caf50',
    '#8bc34a',
    '#cddc39',
    '#ffc107',
    '#ff9800',
    '#ff5722',
    '#795548',
    '#9e9e9e',
    '#607d8b',
    '#212121',
  ]

  const columns = []
  columns.push(
    <TableColumn
      dataKey={'name'}
      editable
      validate={value => typeof value === 'string' && value.length > 0}
      collapsable={false}
      key={'columnName'}>
      {t('Name')}
    </TableColumn>,
  )
  columns.push(
    <TableColumn
      dataKey={'price'}
      editable
      validate={value => {
        if (typeof value === 'string')
          value = parseFloat(value.replace(',', '.'))
        return !isNaN(value) && value >= 0
      }}
      transform={value => {
        if (isNaN(value)) return value
        return value.toFixed(2).toString().replace('.', ',')
      }}
      reverseTransform={value => parseFloat(value.replace(',', '.'))}
      cellStyle={{ textAlign: 'right' }}
      key={'columnPrice'}
      sortable={false}>
      {t('Price')}
    </TableColumn>,
  )

  if (preferences && preferences.onlinePaymentEnabled) {
    columns.push(
      <TableColumn
        dataKey={'deposit'}
        editable
        validate={value => {
          if ('' === value) value = '0'
          if (typeof value === 'string')
            value = parseFloat(value.replace(',', '.'))
          return !isNaN(value) && value >= 0
        }}
        transform={value => {
          if (isNaN(value)) return value
          if (null === value) return '0,00'
          return value.toFixed(2).toString().replace('.', ',')
        }}
        reverseTransform={value => parseFloat(value.replace(',', '.'))}
        cellStyle={{ textAlign: 'right' }}
        collapsed
        key={'columnDeposit'}
        sortable={false}>
        {t('Deposit')}
      </TableColumn>,
    )
  }

  columns.push(
    <TableColumn
      dataKey={'duration'}
      reverseTransform={value => Number(value)}
      editable
      choices={minuteChoices}
      key={'columnDuration'}>
      {t('Duration')}
    </TableColumn>,
  )

  columns.push(
    <TableColumn
      dataKey={'wait_time'}
      editable
      choices={minuteChoices}
      collapsed
      key={'columnWaitTime'}>
      {t('Wait time')}
    </TableColumn>,
  )

  columns.push(
    <TableColumn
      dataKey={'color'}
      editable
      colors={colors}
      collapsed
      key={'columnColor'}>
      {t('Color')}
    </TableColumn>,
  )

  columns.push(
    <TableColumn
      dataKey={'category_id'}
      editable
      choices={categoryChoices}
      collapsed
      key={'columnCategory'}
      sortable>
      {t('Category')}
    </TableColumn>,
  )

  columns.push(
    <TableColumn
      dataKey={'is_person_selectable'}
      editable
      cellStyle={{ textAlign: 'right' }}
      choices={{ true: true, false: false }}
      collapsed
      key={'columnPersonSelectable'}>
      {t('Person selectable')}
    </TableColumn>,
  )

  columns.push(
    <TableColumn
      dataKey={'is_online'}
      editable
      cellStyle={{ textAlign: 'right' }}
      choices={{ true: true, false: false }}
      collapsed
      key={'columnIsOnline'}>
      {t('is online')}
    </TableColumn>,
  )

  if (departments && departments.length > 1) {
    columns.push(
      <TableColumn
        dataKey={'department_id'}
        editable
        choices={departmentChoices}
        collapsed
        key={'columnDepartment'}
        sortable={false}>
        {t('Department')}
      </TableColumn>,
    )
  }

  return (
    <>
      <h2>{t('Activities')}</h2>
      <Link to={'/settings/activities/mass-edit-cabins-devices'}>
        {t('Edit cabins and devices')}
      </Link>
      <TableComponent dataProvider={dataProvider} pageSize={25}>
        {columns}
      </TableComponent>
    </>
  )
}
